import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EvalFixtureSet, RunPromptTestResponse } from '../api/promptTestingApi';
import { useRunPromptTestMutation } from '../api/promptTestingApi';
import { ComparisonOutput } from './ComparisonOutput';

export function formatPromptTestingError(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const data = (err as { data?: unknown }).data;
    if (typeof data === 'object' && data !== null && 'error' in data) {
      const message = String((data as { error?: unknown }).error ?? '');
      if (message.includes('No coaching relationships')) return message;
      if (message.includes('too many concurrent'))
        return 'You already have two prompt tests running. Wait for one to finish and try again.';
      if (message.includes('unable to identify user'))
        return 'Your session could not be verified. Refresh the page and sign in again.';
      if (message.includes('fixture set must have passed scenario anonymization'))
        return 'This test persona is no longer available for beta prompt tests. Create a new anonymized persona and try again.';
      if (message.includes('fixture set run gate is not configured'))
        return 'Beta prompt tests are not fully configured yet. Create a new anonymized persona after setup is complete.';
      return 'Prompt test failed. Check the backend logs for details.';
    }
  }

  return 'Prompt test failed. Check the backend logs for details.';
}

interface TestColumnProps {
  profileSlug: string;
  promptSlug: string;
  candidateText: string;
  fixtures: EvalFixtureSet[];
  preferredFixtureSetId?: string | null;
  onPreferredFixtureApplied: () => void;
  onBack: () => void;
  onCreateFixture: () => void;
}

const STORAGE_KEY = 'prompt-tester:fixture-id';
export const DEFAULT_USER_MESSAGE =
  'Tell me about my recent coaching session and what I should focus on.';

export function shouldDisableRunComparison(
  userMessage: string,
  isRunning: boolean,
  isRunningComparison: boolean,
  fixtureSetId: string
): boolean {
  return isRunning || isRunningComparison || !userMessage.trim() || !fixtureSetId;
}

export function shouldShowUserMessageEditor(showAdvancedOptions: boolean): boolean {
  return showAdvancedOptions;
}

export function testColumnSetupMessage(
  hasFixtures: boolean,
  fixtureSetId: string,
  userMessage: string
): string {
  if (!hasFixtures) {
    return 'Create an anonymized test persona before running persona-backed comparisons.';
  }
  if (!fixtureSetId) {
    return 'Select a test persona before running a comparison.';
  }
  if (!userMessage.trim()) {
    return 'Add a test message before running a comparison.';
  }
  return 'Run a comparison to see baseline and candidate outputs side by side.';
}

export interface ComparisonInputKeyParts {
  profileSlug: string;
  promptSlug: string;
  candidateText: string;
  userMessage: string;
  fixtureSetId: string;
}

export function buildComparisonInputKey(parts: ComparisonInputKeyParts): string {
  return JSON.stringify([
    parts.profileSlug,
    parts.promptSlug,
    parts.candidateText,
    parts.userMessage,
    parts.fixtureSetId,
  ]);
}

export function shouldResetComparisonResults(previousKey: string, nextKey: string): boolean {
  return previousKey !== nextKey;
}

export function shouldApplyAsyncComparisonResults(
  isMounted: boolean,
  currentKey: string,
  runKey: string
): boolean {
  return isMounted && currentKey === runKey;
}

export function shouldClearAsyncLoadingState(
  isMounted: boolean,
  currentRunId: number,
  runId: number
): boolean {
  return isMounted && currentRunId === runId;
}

export function selectFixtureSetId(
  currentFixtureSetId: string,
  fixtures: Array<{ id: string }>,
  storedFixtureSetId: string | null,
  hasExplicitFixtureSelection = false
): string {
  if (hasExplicitFixtureSelection) {
    return currentFixtureSetId && fixtures.some((fixture) => fixture.id === currentFixtureSetId)
      ? currentFixtureSetId
      : '';
  }
  if (currentFixtureSetId && fixtures.some((fixture) => fixture.id === currentFixtureSetId)) {
    return currentFixtureSetId;
  }
  if (storedFixtureSetId && fixtures.some((fixture) => fixture.id === storedFixtureSetId)) {
    return storedFixtureSetId;
  }
  return fixtures[0]?.id ?? '';
}

export function TestColumn({
  profileSlug,
  promptSlug,
  candidateText,
  fixtures,
  preferredFixtureSetId,
  onPreferredFixtureApplied,
  onBack: _onBack,
  onCreateFixture,
}: TestColumnProps) {
  const [fixtureSetId, setFixtureSetId] = useState<string>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return selectFixtureSetId('', fixtures, stored);
  });
  const [hasExplicitFixtureSelection, setHasExplicitFixtureSelection] = useState(false);
  const [userMessage, setUserMessage] = useState(DEFAULT_USER_MESSAGE);
  const [baseResult, setBaseResult] = useState<RunPromptTestResponse | null>(null);
  const [candidateResult, setCandidateResult] = useState<RunPromptTestResponse | null>(null);
  const [isRunningComparison, setIsRunningComparison] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const [runTest, { isLoading: isRunning }] = useRunPromptTestMutation();
  const isMountedRef = useRef(true);
  const comparisonRunIdRef = useRef(0);
  const comparisonInputKey = buildComparisonInputKey({
    profileSlug,
    promptSlug,
    candidateText,
    userMessage,
    fixtureSetId,
  });
  const comparisonInputKeyRef = useRef(comparisonInputKey);
  const hasFixtures = fixtures.length > 0;

  // Persist fixture selection
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setFixtureSetId((current) =>
      selectFixtureSetId(
        current,
        fixtures,
        localStorage.getItem(STORAGE_KEY),
        hasExplicitFixtureSelection
      )
    );
  }, [fixtures, hasExplicitFixtureSelection]);

  useEffect(() => {
    if (fixtureSetId) {
      localStorage.setItem(STORAGE_KEY, fixtureSetId);
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
  }, [fixtureSetId]);

  useEffect(() => {
    if (!preferredFixtureSetId) return;
    if (!fixtures.some((fixture) => fixture.id === preferredFixtureSetId)) return;

    setFixtureSetId(preferredFixtureSetId);
    setHasExplicitFixtureSelection(true);
    onPreferredFixtureApplied();
  }, [preferredFixtureSetId, fixtures, onPreferredFixtureApplied]);

  useEffect(() => {
    if (!shouldResetComparisonResults(comparisonInputKeyRef.current, comparisonInputKey)) return;

    comparisonInputKeyRef.current = comparisonInputKey;
    setBaseResult(null);
    setCandidateResult(null);
  }, [comparisonInputKey]);

  const handleRunComparison = useCallback(async () => {
    if (!userMessage.trim()) return;
    if (!fixtureSetId) return;

    const runInputKey = comparisonInputKey;
    const runId = comparisonRunIdRef.current + 1;
    comparisonRunIdRef.current = runId;
    setIsRunningComparison(true);
    setBaseResult(null);
    setCandidateResult(null);

    const baseReq = {
      profile_slug: profileSlug,
      user_message: userMessage,
      fixture_set_id: fixtureSetId,
    };

    const candidateReq = {
      ...baseReq,
      prompt_override: {
        slug: promptSlug,
        text: candidateText,
      },
    };

    // Fire both in parallel
    try {
      const [baseResp, candidateResp] = await Promise.all([
        runTest(baseReq)
          .unwrap()
          .catch((err: unknown) => ({
            output: '',
            has_fixture_context: !!fixtureSetId,
            has_document_context: false,
            document_count: 0,
            error: { message: formatPromptTestingError(err), type: 'internal' as const },
          })),
        runTest(candidateReq)
          .unwrap()
          .catch((err: unknown) => ({
            output: '',
            has_fixture_context: !!fixtureSetId,
            has_document_context: false,
            document_count: 0,
            error: { message: formatPromptTestingError(err), type: 'internal' as const },
          })),
      ]);

      if (
        !shouldApplyAsyncComparisonResults(
          isMountedRef.current,
          comparisonInputKeyRef.current,
          runInputKey
        )
      )
        return;

      setBaseResult(baseResp as RunPromptTestResponse);
      setCandidateResult(candidateResp as RunPromptTestResponse);
    } finally {
      if (shouldClearAsyncLoadingState(isMountedRef.current, comparisonRunIdRef.current, runId)) {
        setIsRunningComparison(false);
      }
    }
  }, [
    fixtureSetId,
    userMessage,
    profileSlug,
    promptSlug,
    candidateText,
    runTest,
    comparisonInputKey,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Config section */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" fontWeight={600} fontSize={13}>
            Test persona comparison
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel sx={{ fontSize: 12 }}>Test Persona</InputLabel>
            <Select
              value={fixtureSetId}
              label="Test Persona"
              onChange={(e) => {
                setHasExplicitFixtureSelection(true);
                setFixtureSetId(e.target.value);
              }}
              sx={{ fontSize: 12 }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>
                Select a test persona
              </MenuItem>
              {fixtures.map((f) => (
                <MenuItem key={f.id} value={f.id} sx={{ fontSize: 12 }}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={onCreateFixture}
            sx={{ fontSize: 11, textTransform: 'none', minWidth: 'auto', px: 1 }}
          >
            Create persona
          </Button>
        </Box>

        {(!fixtureSetId || !hasFixtures || !userMessage.trim()) && (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            action={
              !hasFixtures ? (
                <Button color="inherit" size="small" onClick={onCreateFixture}>
                  Create test persona
                </Button>
              ) : undefined
            }
          >
            {testColumnSetupMessage(hasFixtures, fixtureSetId, userMessage)}
          </Alert>
        )}

        <Button
          size="small"
          variant="text"
          onClick={() => setShowAdvancedOptions((value) => !value)}
          sx={{ fontSize: 11, textTransform: 'none', px: 0, mb: 0.5 }}
        >
          {showAdvancedOptions ? 'Hide advanced message' : 'Advanced: edit test message'}
        </Button>

        <Collapse in={shouldShowUserMessageEditor(showAdvancedOptions)}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={3}
            label="Test message"
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            helperText="Default message is used unless you need to test a specific input."
            sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: 12 } }}
          />
        </Collapse>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleRunComparison}
            disabled={shouldDisableRunComparison(
              userMessage,
              isRunning,
              isRunningComparison,
              fixtureSetId
            )}
            sx={{ fontSize: 12, textTransform: 'none', flex: 1 }}
          >
            {isRunning || isRunningComparison ? (
              <CircularProgress size={16} sx={{ mr: 0.5 }} />
            ) : null}
            Run comparison
          </Button>
        </Box>
      </Box>

      {/* Results section */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {baseResult && candidateResult ? (
          <ComparisonOutput base={baseResult} candidate={candidateResult} />
        ) : null}

        {(isRunning || isRunningComparison) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!baseResult && !candidateResult && !isRunning && !isRunningComparison && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {testColumnSetupMessage(hasFixtures, fixtureSetId, userMessage)}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
